import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCommandStore } from '../../store/useCommandStore';
import {
  Bell,
  Search,
  User,
  LogOut,
  Menu,
  Settings,
  Check,
  FileText,
  Clock,
  Calendar
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const TopBar = ({ onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const { setOpen } = useCommandStore();
  const location = useLocation();

  const getPageTitle = (pathname) => {
    switch (pathname) {
      case '/': return 'Dashboard';
      case '/dispatch': return 'Dispatch Register';
      case '/diary': return 'Diary Register';
      case '/compliance': return 'Compliance Tracking';
      case '/reports': return 'Reports';
      case '/search': return 'Search Records';
      case '/users': return 'User Management';
      case '/audit': return 'Audit Trail';
      case '/offices': return 'Office Management';
      case '/profile': return 'My Profile';
      case '/notifications': return 'Notifications';
      default: return 'Office DAK System';
    }
  };

  const currentDate = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-indigo-100/90 bg-[linear-gradient(135deg,rgba(255,255,255,0.82),rgba(240,245,255,0.78),rgba(245,243,255,0.75))] px-3 shadow-[0_12px_30px_rgba(99,102,241,0.08)] backdrop-blur-2xl sm:px-6">
      <div className="flex min-w-0 items-center gap-2 sm:gap-4">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-100 bg-white/70 text-indigo-700 shadow-sm transition-all hover:bg-white hover:shadow-md 2xl:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h2 className="truncate bg-gradient-to-r from-slate-900 via-indigo-900 to-violet-700 bg-clip-text text-base font-bold tracking-tight text-transparent sm:text-xl">
            {getPageTitle(location.pathname)}
          </h2>
          <div className="mt-0.5 hidden items-center text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-600 lg:flex">
            <Calendar className="mr-1.5 h-3.5 w-3.5" />
            {currentDate}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <button
          onClick={() => setOpen(true)}
          className="hidden w-52 items-center justify-between gap-2 rounded-2xl border border-indigo-100 bg-white/75 px-3 py-2 text-sm text-slate-600 shadow-[0_8px_20px_rgba(99,102,241,0.06)] transition-all hover:border-indigo-200 hover:bg-white hover:text-indigo-700 lg:flex xl:w-64"
        >
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-indigo-500" />
            <span>Search...</span>
          </div>
          <kbd className="inline-flex h-5 items-center gap-1 rounded border border-indigo-100 bg-indigo-50 px-1.5 font-mono text-[10px] font-medium text-indigo-600">
            <span className="text-[10px]">Ctrl</span>K
          </kbd>
        </button>

        <div className="hidden h-7 w-px bg-indigo-100 lg:block" />

        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/notifications"
            className="relative rounded-full border border-indigo-100 bg-white/70 p-2.5 text-indigo-600 shadow-sm transition-all duration-200 hover:border-indigo-200 hover:bg-white hover:text-indigo-700"
            title="Notifications"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute right-2 top-2 inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-red-500 ring-2 ring-white" />
            )}
          </Link>

          <div className="hidden h-8 w-px bg-indigo-100 sm:block" />

          <Link
            to="/profile"
            className="group flex items-center gap-2 rounded-full border border-indigo-100 bg-white/70 p-1 pr-2 shadow-sm transition-all duration-200 hover:border-indigo-200 hover:bg-white sm:gap-3"
            title="Profile"
          >
            <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-indigo-200 bg-gradient-to-br from-indigo-100 to-violet-100 text-sm font-bold text-indigo-700 shadow-sm transition-transform group-hover:scale-105">
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture.startsWith('http') ? user.profilePicture : `/api/users/${user.id || user.userId}/profile-picture?token=${localStorage.getItem('token')}`}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                user?.name?.charAt(0)
              )}
            </div>
            <div className="hidden flex-col items-start lg:flex">
              <span className="text-sm font-semibold text-slate-700 group-hover:text-indigo-700">{user?.name}</span>
              <span className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-indigo-500">{user?.role}</span>
            </div>
          </Link>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
